import { useEffect, useMemo, useState } from "react"
import clsx from "clsx"
import { getData as getCountryData } from "country-list"

import { gameService } from "services/game/game.service"

export type GeoSelectorScope =
  | { kind: "category_order" } // Homepage tab — fork = CategoryOrder row
  | { kind: "game_order" } // Sections / Categories tab — fork = GameCategoryOrder row in any category
  | { kind: "game_order_for_category"; categoryKey: string } // SectionOrderEditor drill-in
  | { kind: "any" } // fallback — union of both tables

interface GeoSelectorProps {
  value: string // '*' or ISO-2
  onChange: (geo: string) => void
  className?: string
  // Scope decides which countries get flagged as forked. Without it the
  // selector flags any country with ANY override — confusing on a tab that
  // only cares about one ordering table.
  scope?: GeoSelectorScope
  refreshKey?: number
}

const DEFAULT_OPTION = { code: "*", name: "Global default (*)" }

export const GeoSelector = ({ value, onChange, className, scope = { kind: "any" }, refreshKey = 0 }: GeoSelectorProps) => {
  const [forkedSet, setForkedSet] = useState<Set<string>>(new Set())

  const scopeSerialised = scope.kind === "game_order_for_category" ? `game_order:${scope.categoryKey}` : scope.kind

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      const apiScope = scope.kind === "category_order" ? "category_order" : scope.kind === "any" ? "any" : "game_order"
      const categoryParam = scope.kind === "game_order_for_category" ? scope.categoryKey : undefined
      const geos = await gameService.listForkedGeos(apiScope, categoryParam)
      if (cancelled) return
      setForkedSet(new Set(geos ?? []))
    }
    load()
    return () => {
      cancelled = true
    }
  }, [refreshKey, value, scopeSerialised])

  const { forkedOptions, restOptions } = useMemo(() => {
    const countries = getCountryData()
      .map((c) => ({ code: c.code, name: c.name }))
      .sort((a, b) => a.name.localeCompare(b.name))
    const forked = countries.filter((c) => forkedSet.has(c.code))
    const rest = countries.filter((c) => !forkedSet.has(c.code))
    return { forkedOptions: forked, restOptions: rest }
  }, [forkedSet])

  const valueIsForked = value !== "*" && forkedSet.has(value)

  return (
    <div className={clsx("flex flex-col gap-2", className)}>
      <label className="flex items-center gap-2 text-sm text-[rgba(6,7,21,1)] dark:text-white flex-wrap">
        <span className="opacity-70">Editing for</span>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={clsx(
            "select select-bordered select-sm bg-white text-black border-black/10 dark:bg-[#0C0C1A] dark:text-white dark:border-white/10",
            valueIsForked && "border-[rgba(132,_0,_168,_1)] dark:border-[rgba(132,_0,_168,_1)]"
          )}
        >
          <option value={DEFAULT_OPTION.code}>{DEFAULT_OPTION.name}</option>
          {forkedOptions.length > 0 && (
            <optgroup label={`★ Forked (${forkedOptions.length})`}>
              {forkedOptions.map((o) => (
                <option key={o.code} value={o.code}>
                  ★ {o.code} — {o.name}
                </option>
              ))}
            </optgroup>
          )}
          <optgroup label="All countries">
            {restOptions.map((o) => (
              <option key={o.code} value={o.code}>
                {o.code} — {o.name}
              </option>
            ))}
          </optgroup>
        </select>
      </label>

      {forkedOptions.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs opacity-60 text-[rgba(6,7,21,1)] dark:text-white">Quick switch:</span>
          <ForkPill code="*" label="Default" active={value === "*"} onClick={() => onChange("*")} />
          {forkedOptions.map((o) => (
            <ForkPill key={o.code} code={o.code} label={o.code} active={value === o.code} onClick={() => onChange(o.code)} />
          ))}
        </div>
      )}
    </div>
  )
}

interface ForkPillProps {
  code: string
  label: string
  active: boolean
  onClick: () => void
}

const ForkPill = ({ code, label, active, onClick }: ForkPillProps) => (
  <button
    type="button"
    onClick={onClick}
    title={code === "*" ? "Switch to global default" : `Switch to ${code} fork`}
    className={clsx(
      "px-2 py-0.5 rounded-full text-xs font-medium border transition-colors",
      active
        ? "bg-[rgba(132,_0,_168,_1)] border-[rgba(132,_0,_168,_1)] text-white"
        : "border-[rgba(132,_0,_168,_0.35)] text-[rgba(132,_0,_168,_1)] hover:bg-[rgba(132,_0,_168,_0.08)] dark:text-[rgba(217,_140,_237,_1)] dark:border-[rgba(132,_0,_168,_0.6)] dark:hover:bg-[rgba(132,_0,_168,_0.15)]"
    )}
  >
    {code !== "*" && "★ "}
    {label}
  </button>
)
